import React, { useState, useRef, useMemo, useEffect } from "react";
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { useLeaveEncashment } from "../../../hooks/useLeaveEncashment";
import { useFilterManager } from "../../../hooks/useFilterManager";
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { Button, SearchBar } from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";
import { ConfirmationModal } from "../../../../common/Modal";
import { buildMasterlistWorksheet } from "../../../../utils/employee/buildWorksheet";
import { FEATURES } from "../../../../config/features";
import LeaveEncashmentTable from "./components/LeaveEncashmentTable";
import LeaveEncashmentBreakdown from "./components/LeaveEncashmentBreakdown";
import LeaveEncashmentProcessModal from "./components/LeaveEncashmentProcessModal";
import { useHRCalendar } from "../../../hooks/useHRCalendar";
import { RevertLeaveEncashmentForm } from "./components/RevertLeaveEncashmentForm";
import { useSimulateLoading } from "../../../hooks/useSimulateLoading";
import { CustomLoader } from "../Payroll/utils/CustomLoader";

const LeaveEncashmentModule = ({ user, showGeneralMessage }) => {
  const [showSettings, setShowSettings] = useState(false);
  const settingRef = useRef(null);

  // Modal & Selection States
  const [currentYear, setCurrentYear] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]); // Used for Main Table View
  const [targetPayrollPeriodId, setTargetPayrollPeriodId] = useState(null);
  const [targetClassificationId, setTargetClassificationId] = useState(null);
  const [targetProcess, setTargetProcess] = useState("payroll");
  const [processingIds, setProcessingIds] = useState([]); // ADDED: To hold IDs specifically for the Sync Modal

  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isFinalConfirmOpen, setIsFinalConfirmOpen] = useState(false);

  const [showRevertLeaveEncashmentForm, setShowRevertLeaveEncashmentForm] = useState(false);

  const [form, setForm] = useState(null);
  // -- Hooks ---
  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    applyFilters,
    resetFilters
  } = useFilterManager(FEATURES.EMPLOYEE_FILTERS || []);

  const { currentYearData, fetchCurrentYear } = useHRCalendar({ showGeneralMessage, isMainModule: false });

  const {
    loading, data, setData, handleSearch,
    postToPayroll,
    revertLeaveEncashment,
    refresh,
    setLoading
  } = useLeaveEncashment({ user, currentYear, showGeneralMessage, apiFilterPayload });

  const {
    progress,
    loadingPercentage,
    setLoadingPercentage,
    longLoadingRequest,
    setLongLoadingRequest,
    simulateLoading
  } = useSimulateLoading();

  const syncSummaryData = useMemo(() => {
    // This will be populated by the modal's internal selection
    return processingIds.map(id => data.find(emp => emp.id === id)).filter(Boolean);
  }, [data, processingIds]);

  const totalAmountToPost = useMemo(() => {
    return syncSummaryData.reduce((sum, item) => sum + parseFloat(item.totalGross || 0), 0);
  }, [syncSummaryData]);


  const openRevertLeaveEncashmentForm = () => {
    setShowRevertLeaveEncashmentForm(true);
  }
  const closeRevertLeaveEncashmentForm = () => {
    setShowRevertLeaveEncashmentForm(false);
  }

  const handleOpenSyncModal = () => {
    setIsSyncModalOpen(true);
  };

  // MODIFIED: Receives Period, Process, AND the selected IDs from the Modal
  const handleProceedToFinalConfirm = (periodId, classificationId, process, idsToProcess) => {
    if (idsToProcess.length === 0) {
      showGeneralMessage("Please select at least one employee inside the process modal.", false);
      return;
    }
    setProcessingIds(idsToProcess);
    setTargetPayrollPeriodId(periodId);
    setTargetClassificationId(classificationId);
    setTargetProcess(process);
    setIsSyncModalOpen(false);
    setIsFinalConfirmOpen(true);
  };

  const handleFinalExecuteSync = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    try {
      setLoading(true);
      // Pass the processingIds collected from the modal
      const success = await postToPayroll(processingIds, targetPayrollPeriodId, targetClassificationId, targetProcess);

      if (success) {
        setProcessingIds([]);
        setTargetPayrollPeriodId(null);
        setTargetClassificationId(null);
        setIsFinalConfirmOpen(false);
        await refresh();
      }

      // success message is inside the postToPayroll.
      showGeneralMessage(`Selected employees pushed to ${targetProcess} staging!`, true);
    } catch (error) {
      console.error("Error:", error);

      let errorMessage = "Error processing request!";

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data || "Server Error: " + error.response.status;
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = error.message;
      }
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoading(false);
    }

  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const section = {
      label: "Leave Inquiry Summary",
      fields: [
        { key: "employeeId", label: "Emp ID", width: 12 },
        { key: "fullName", label: "Name", width: 30 },
        { key: "totalLeaveBalance", label: "Total Bal", width: 12 },
        { key: "totalEncashable", label: "Encashable", width: 12 },
        { key: "totalGross", label: "Total Value", width: 15 },
      ]
    };
    buildMasterlistWorksheet(workbook, "Summary", section, data);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Leave_Encashment_Summary.xlsx`);
    setShowSettings(false);
  };

  useEffect(() => {
    fetchCurrentYear();
  }, [fetchCurrentYear]);

  useEffect(() => {
    if (currentYearData?.year) {
      setCurrentYear(currentYearData.year);
    }
  }, [currentYearData]);

  return (
    <div className="relative">
      <LoadingSpinner loading={loading} />

      <div className="w-full py-2 px-4 box-border">
        <div className="text-sm">
          <span className="text-gray-400">{"Transaction > "}</span>
          <span className="border-b border-black text-gray-700">{"Leave Encashment"}</span>
        </div>
        <div className="flex items-center justify-between border-b bg-white">
          <h3 className="text-xl font-bold m-0 text-customMainColor tracking-tight uppercase">
            Leave Encashment
          </h3>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700">
              <span className="text-gray-500">Current Year:</span>
              <span className="font-semibold text-gray-700">{currentYear}</span>
            </div>
            <div className="relative flex items-center" ref={settingRef}>
              <button onClick={() => setShowSettings(!showSettings)} className="flex items-center gap-1 bg-blue-800 p-2 rounded hover:bg-blue-900 border-none cursor-pointer shadow-sm transition-all">
                <SettingsIcon className="text-white" fontSize="small" />
                <KeyboardArrowDownIcon className="text-white" fontSize="small" />
              </button>
              {showSettings && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white shadow-2xl border border-gray-200 rounded-lg z-50 overflow-hidden animate-in fade-in zoom-in duration-100">
                  {/* Not be using for now */}
                  <button
                    onClick={handleExport}
                    className="hidden w-full flex items-center justify-start gap-2 px-4 py-3 text-left hover:bg-blue-50 border-none bg-transparent cursor-pointer text-gray-700 transition-colors"
                  >
                    <FileDownloadIcon fontSize="small" className="text-blue-600 shrink-0" />
                    <span className="text-sm font-medium">Export Summary to Excel</span>
                  </button>

                  <button
                    onClick={openRevertLeaveEncashmentForm}
                    className="w-full flex items-center justify-start gap-2 px-4 py-3 text-left hover:bg-blue-50 border-none bg-transparent cursor-pointer text-gray-700 transition-colors"
                  >
                    <SettingsBackupRestoreIcon fontSize="small" className="text-red-500 shrink-0" />
                    <span className="text-sm font-medium">Revert</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
          <div className="border-b border-gray-100 mb-4">
            <span className="text-lg font-semibold text-gray-600">Filters</span>
          </div>
          <DynamicFilterBar requestedFilters={FEATURES.EMPLOYEE_FILTERS || []} activeFilterState={activeFilterState} onFilterChange={onFilterChange} showButtons={false} />
          <div className="flex gap-2 mt-5 border-t border-gray-200 pt-4">
            <Button name="GENERATE" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 border-none shadow cursor-pointer font-bold rounded-lg" onClick={applyFilters} />
            <Button name="POST/SYNC DATA" icon={<SyncAltIcon />} className="bg-green-600 hover:bg-green-700 text-white px-5 py-3 border-none shadow cursor-pointer font-bold rounded-lg" onClick={handleOpenSyncModal} />
            <Button name="CLEAR" className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-3 border-none shadow cursor-pointer rounded-lg" onClick={() => { resetFilters(); setData([]); }} />
          </div>
        </div>

        <SearchBar handleFilter={handleSearch} />

        <LeaveEncashmentTable data={data} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onViewDetail={(emp) => setSelectedEmployee(emp)} />

        <LeaveEncashmentBreakdown isOpen={!!selectedEmployee} onClose={() => setSelectedEmployee(null)} employee={selectedEmployee} />

        {/* MODIFIED: Changed component and props */}
        <LeaveEncashmentProcessModal
          user={user}
          isOpen={isSyncModalOpen}
          onClose={() => setIsSyncModalOpen(false)}
          onConfirm={handleProceedToFinalConfirm}
          currentYear={currentYear}
          showGeneralMessage={showGeneralMessage}
        />

        <RevertLeaveEncashmentForm
          isOpen={showRevertLeaveEncashmentForm}
          onClose={closeRevertLeaveEncashmentForm}
          progress={progress}
          simulateLoading={simulateLoading}
          setLoadingPercentage={setLoadingPercentage}
          setLoadingRequest={setLongLoadingRequest}
          refresh={refresh}
          showGeneralMessage={showGeneralMessage}
          revertLeaveEncashment={revertLeaveEncashment}
        />

        <ConfirmationModal
          visible={isFinalConfirmOpen}
          onClose={() => setIsFinalConfirmOpen(false)}
          onConfirm={handleFinalExecuteSync}
          title={`Confirm Posting to ${targetProcess === 'payroll' ? 'Regular Payroll' : 'Final Pay'}`}
          message={`Are you sure you want to proceed with posting encashments for ${processingIds.length} employees?`}
          noteMessage={`Total amount to be credited: ₱${totalAmountToPost.toLocaleString()}. This will create entries in the ${targetProcess} staging table.`}
          confirmText="Yes, Proceed"
          type="info"
          isLoading={loading}
        />

        {longLoadingRequest ? (
          <CustomLoader loadingPercentage={loadingPercentage} />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default LeaveEncashmentModule;